import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserSearch } from '../components/reports/UserSearch';
import { UserReportView } from '../components/reports/UserReportView';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { getModuleIcon } from '../lib/moduleIcons';
import { EEOCHistoryDialog } from '../components/eeoc/EEOCHistoryDialog';
import { EEOCReportControls, filterEEOCRows } from '../components/eeoc/EEOCReportControls';
import { EEOCNudgeCell } from '../components/eeoc/EEOCNudgeControls';
import { RolePlayLabReport } from '../components/reports/RolePlayLabReport';
import { ReportArchive } from '../components/reports/ReportArchive';
import {
    Archive,
    ClipboardCheck,
    Drama,
    BarChart3,
    TrendingUp,
    TrendingDown,
    Calendar,
    Filter,
    Target,
    Award,
    Mic,
    Briefcase,
    Search,
    Video,
    Users,
    Clock,
    FileText,
    Loader2,
    RefreshCw,
    Download,
    FileSpreadsheet,
    Building2,
    Flame,
    Activity,
    CalendarDays,
    ChevronDown,
    ChevronRight,
    GraduationCap,
    Sparkles,
    Eye,
    CheckCircle,
    AlertCircle,
    Zap,
    DollarSign,
    Scale
} from 'lucide-react';

const Reports = () => {
    const { api, user, isAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('simulations');
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [generatingScores, setGeneratingScores] = useState(false);
    const [scoreSummary, setScoreSummary] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set()); // For expandable rows in Tech Learning
    const [courseModules, setCourseModules] = useState([]); // Admin-created course modules
    const [eeocHistoryUser, setEeocHistoryUser] = useState(null);
    const [eeocPeriod, setEeocPeriod] = useState('');
    const [eeocFilter, setEeocFilter] = useState('all');
    const [roleplayView, setRoleplayView] = useState('sessions');
    const [reportUser, setReportUser] = useState(null);
    
    // Toggle expanded row
    const toggleRowExpand = (userId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };
    
    // Filters
    const [timeRange, setTimeRange] = useState('30');
    const [department, setDepartment] = useState('all');
    const [month, setMonth] = useState('');

    // Universal Score Generator
    const handleGenerateScores = async () => {
        setGeneratingScores(true);
        try {
            const response = await api.post('/reports/generate-scores', null, {
                params: { report_type: 'all' }
            });
            const results = response.data.results;
            toast.success(
                `Scores generated! Fixed: ${results.simulations_fixed} simulations, ${results.sourcing_labs_fixed} labs, ${results.job_simulations_fixed} job sims, ${results.evolve_fixed} evolve sessions`
            );
            // Refresh the current report
            fetchReport();
            fetchScoreSummary();
        } catch (error) {
            toast.error('Failed to generate scores');
        } finally {
            setGeneratingScores(false);
        }
    };

    // Fetch comprehensive score summary
    const fetchScoreSummary = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const params = new URLSearchParams();
            if (timeRange !== 'all') params.append('days', timeRange);
            if (department !== 'all') params.append('department', department);
            
            const response = await api.get(`/reports/score-summary?${params}`);
            setScoreSummary(response.data);
        } catch (error) {
            console.error('Failed to fetch score summary:', error);
        }
    }, [api, timeRange, department, isAdmin]);

    // Handle row click for detail popup
    const handleRowClick = (row) => {
        setSelectedRow(row);
        setShowDetailModal(true);
    };

    const fetchDepartments = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const response = await api.get('/users/departments/list');
            setDepartments(response.data.departments || []);
        } catch (error) {
            console.error('Failed to fetch departments:', error);
        }
    }, [api, isAdmin]);

    const fetchCourseModules = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const response = await api.get('/course-modules');
            setCourseModules(response.data.modules || []);
        } catch (error) {
            console.error('Failed to fetch course modules:', error);
        }
    }, [api, isAdmin]);

    const fetchReport = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const params = new URLSearchParams();
            if (timeRange !== 'all') params.append('days', timeRange);
            if (department !== 'all' && isAdmin) params.append('department', department);
            if (month && activeTab === 'evolve') params.append('month', month);
            if (eeocPeriod && activeTab === 'eeoc_training') params.append('period', eeocPeriod);
            
            let endpoint = '/reports/simulations/detailed';
            if (activeTab === 'report_archive') {
                setReportData(null);
                return;
            }
            if (activeTab.startsWith('module:')) {
                const response = await api.get(`/reports/modules/${activeTab.slice(7)}/detailed?${params}`);
                setReportData(response.data);
                return;
            }
            switch (activeTab) {
                case 'rpat':
                    endpoint = '/reports/rpat/detailed';
                    break;
                case 'tech_learning':
                    endpoint = '/reports/tech-learning/detailed';
                    break;
                case 'finance_training':
                    endpoint = '/reports/finance-training/detailed';
                    break;
                case 'eeoc_training':
                    endpoint = '/eeoc-training/admin/report';
                    break;
                case 'roleplay_labs':
                    endpoint = '/reports/roleplay-labs/detailed';
                    break;
                case 'quality_tasks':
                    endpoint = '/quality-tasks/admin/course-report';
                    break;
                case 'inside_sales':
                    endpoint = '/reports/inside-sales/detailed';
                    break;
                case 'job_simulations':
                    endpoint = '/reports/job-simulations/detailed';
                    break;
                case 'sourcing_labs':
                    endpoint = '/reports/sourcing-labs/detailed';
                    break;
                case 'evolve':
                    endpoint = '/reports/evolve/detailed';
                    break;
                case 'streaks':
                    endpoint = '/reports/streaks/detailed';
                    break;
                case 'last_login':
                    endpoint = '/reports/last-login';
                    break;
                case 'user_activity':
                    endpoint = '/reports/user-activity-summary';
                    break;
                case 'monthly':
                    endpoint = '/reports/monthly-summary';
                    break;
                default:
                    endpoint = '/reports/simulations/detailed';
            }
            
            const response = await api.get(`${endpoint}?${params}`);
            setReportData(response.data);
        } catch (error) {
            toast.error('Failed to load report');
        } finally {
            setLoading(false);
        }
    }, [api, activeTab, timeRange, department, month, isAdmin, eeocPeriod]);
    const refreshQuietly = () => fetchReport({ silent: true });

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (timeRange !== 'all') params.append('days', timeRange);
            if (department !== 'all' && isAdmin) params.append('department', department);
            if (month && activeTab === 'evolve') params.append('month', month);
            if (eeocPeriod && activeTab === 'eeoc_training') params.append('period', eeocPeriod);
            if (activeTab === 'roleplay_labs') params.append('view', roleplayView);
            
            let endpoint = '/reports/simulations/detailed/export';
            switch (activeTab.startsWith('module:') ? 'module' : activeTab) {
                case 'module':
                    endpoint = `/reports/modules/${activeTab.slice(7)}/detailed/export`;
                    break;
                case 'rpat':
                    endpoint = '/reports/rpat/detailed/export';
                    break;
                case 'tech_learning':
                    endpoint = '/reports/tech-learning/detailed/export';
                    break;
                case 'finance_training':
                    endpoint = '/reports/finance-training/detailed/export';
                    break;
                case 'eeoc_training':
                    endpoint = '/eeoc-training/admin/report/export';
                    break;
                case 'roleplay_labs':
                    endpoint = '/reports/roleplay-labs/detailed/export';
                    break;
                case 'quality_tasks':
                    endpoint = '/quality-tasks/admin/course-report/export';
                    break;
                case 'inside_sales':
                    endpoint = '/reports/inside-sales/detailed/export';
                    break;
                case 'job_simulations':
                    endpoint = '/reports/job-simulations/detailed/export';
                    break;
                case 'sourcing_labs':
                    endpoint = '/reports/sourcing-labs/detailed/export';
                    break;
                case 'evolve':
                    endpoint = '/reports/evolve/detailed/export';
                    break;
                case 'streaks':
                    endpoint = '/reports/streaks/detailed/export';
                    break;
                case 'last_login':
                    endpoint = '/reports/last-login/export';
                    break;
                case 'user_activity':
                    endpoint = '/reports/user-activity-summary/export';
                    break;
                case 'monthly':
                    endpoint = '/reports/monthly-summary/export';
                    break;
                default:
                    endpoint = '/reports/simulations/detailed/export';
            }
            
            const response = await api.get(`${endpoint}?${params}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${activeTab === 'roleplay_labs' ? `roleplay_${roleplayView}` : activeTab}_report_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Report exported successfully');
        } catch (error) {
            toast.error('Failed to export report');
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchScoreSummary();
        fetchCourseModules();
    }, [fetchDepartments, fetchScoreSummary, fetchCourseModules]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const reportConfig = {
        rpat: { 
            icon: GraduationCap, 
            color: 'text-violet-500', 
            bgColor: 'bg-violet-500/10', 
            label: 'R-PAT Training',
            columns: ['User', 'Dept', 'Phase 1', 'Phase 2', 'Phase 3', 'Phase 4', 'Phase 5', 'Phase 6', 'Phase 7', 'Phase 8', 'Overall']
        },
        tech_learning: { 
            icon: FileText, 
            color: 'text-teal-500', 
            bgColor: 'bg-teal-500/10', 
            label: 'Tech Learning',
            columns: ['User', 'Department', 'Courses', 'Avg Progress', 'Completed'],
            adminOnly: true,
            expandable: true
        },
        finance_training: { 
            icon: DollarSign, 
            color: 'text-emerald-500', 
            bgColor: 'bg-emerald-500/10', 
            label: 'Finance Training',
            columns: ['User', 'Department', 'Courses', 'Avg Progress', 'Completed'],
            adminOnly: true,
            expandable: true
        },
        eeoc_training: {
            icon: Scale,
            color: 'text-sky-500',
            bgColor: 'bg-sky-500/10',
            label: 'EEOC Training',
            columns: ['User', 'Department', 'This Period', 'Period Modules', 'Completed On', 'All-time', 'Sessions', 'Avg Score', 'Latest', 'Trend', 'Last Activity', 'Reminders', 'History'],
            adminOnly: true
        },
        roleplay_labs: {
            icon: Drama,
            color: 'text-rose-500',
            bgColor: 'bg-rose-500/10',
            label: 'Role-Play Lab',
            columns: [],
            adminOnly: true
        },
        quality_tasks: {
            icon: ClipboardCheck,
            color: 'text-violet-500',
            bgColor: 'bg-violet-500/10',
            label: 'Quality Tasks',
            columns: ['Name', 'Dept', 'Course', 'Avg Score', 'Completed', 'Assigned By'],
            adminOnly: true
        },
        ...Object.fromEntries(courseModules.map(module => [`module:${module.slug}`, {
            icon: getModuleIcon(module.icon),
            color: 'text-orange-500',
            bgColor: 'bg-orange-500/10',
            label: module.name,
            columns: ['User', 'Department', 'Courses', 'Avg Progress', 'Completed'],
            adminOnly: true,
            expandable: true
        }])),
        inside_sales: { 
            icon: TrendingUp, 
            color: 'text-indigo-500', 
            bgColor: 'bg-indigo-500/10', 
            label: 'Inside Sales',
            columns: ['User', 'Department', 'Session', 'Status', 'Overall', 'Quiz', 'Simulation', 'Time', 'Completed']
        },
        simulations: { 
            icon: Mic, 
            color: 'text-blue-500', 
            bgColor: 'bg-blue-500/10', 
            label: 'Recruiter Simulations',
            columns: ['User', 'Department', 'Scenario', 'Score', 'Voice', 'Communication', 'Completed']
        },
        job_simulations: { 
            icon: Briefcase, 
            color: 'text-purple-500', 
            bgColor: 'bg-purple-500/10', 
            label: 'Job Simulations',
            columns: ['User', 'Department', 'Skill', 'Category', 'Score', 'Type', 'Completed']
        },
        sourcing_labs: { 
            icon: Search, 
            color: 'text-emerald-500', 
            bgColor: 'bg-emerald-500/10', 
            label: 'Sourcing Labs',
            columns: ['User', 'Department', 'Lab', 'Difficulty', 'Score', 'Accuracy', 'Completed']
        },
        evolve: { 
            icon: Video, 
            color: 'text-orange-500', 
            bgColor: 'bg-orange-500/10', 
            label: 'Evolve AI',
            columns: ['User', 'Department', 'Topic', 'Score', 'Clarity', 'Confidence', 'Slides', 'Completed']
        },
        streaks: { 
            icon: Flame, 
            color: 'text-red-500', 
            bgColor: 'bg-red-500/10', 
            label: 'Streaks',
            columns: ['User', 'Department', 'Current Streak', 'Longest Streak', 'XP', 'Badges', 'Last Activity']
        },
        last_login: { 
            icon: Clock, 
            color: 'text-slate-500', 
            bgColor: 'bg-slate-500/10', 
            label: 'Last Login',
            columns: ['User', 'Email', 'Department', 'Role', 'Last Login', 'Created At', 'Status'],
            adminOnly: true
        },
        user_activity: { 
            icon: Activity, 
            color: 'text-cyan-500', 
            bgColor: 'bg-cyan-500/10', 
            label: 'User Activity',
            columns: ['User', 'Department', 'Simulations', 'Job Sims', 'Sourcing', 'EVOLVE', 'Total', 'Last Login'],
            adminOnly: true
        },
        monthly: { 
            icon: CalendarDays, 
            color: 'text-indigo-500', 
            bgColor: 'bg-indigo-500/10', 
            label: 'Monthly Summary',
            columns: ['Month', 'Simulations', 'Job Sims', 'Sourcing Labs', 'EVOLVE', 'Total'],
            adminOnly: true
        },
        report_archive: {
            icon: Archive,
            color: 'text-slate-600',
            bgColor: 'bg-slate-500/10',
            label: 'Report Archive',
            columns: [],
            adminOnly: true
        }
    };

    // Filter out admin-only tabs for non-admins
    const availableTabs = Object.entries(reportConfig).filter(
        ([key, config]) => !config.adminOnly || isAdmin
    );

    const currentConfig = reportConfig[activeTab] || reportConfig.simulations;
    const CurrentIcon = currentConfig.icon;

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    const renderTableRow = (item, index, tabKey = activeTab) => {
        // Generate a stable key using available unique identifiers
        const getRowKey = (prefix) => `${prefix}-${item.id || item.session_id || item.simulation_id || item.user_id || item.email || 'row'}-${index}`;
        
        // Admin-created course modules share the Finance Training row layout
        switch (tabKey.startsWith('module:') ? 'finance_training' : tabKey) {
            case 'rpat':
                return (
                    <TableRow key={getRowKey('rpat')}>
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell className="text-sm">{item.department || '-'}</TableCell>
                        {/* Phase 1-8 columns */}
                        {item.phases?.map((phase, pIdx) => (
                            <TableCell key={`phase-${pIdx}`} className="text-center">
                                {phase.status === 'completed' ? (
                                    <div className="flex flex-col items-center gap-1">
                                        <Badge className="bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/30">
                                            Done
                                        </Badge>
                                        {phase.score !== null && (
                                            <span className="text-xs font-medium text-emerald-600">
                                                {phase.score}%
                                            </span>
                                        )}
                                    </div>
                                ) : phase.status === 'in_progress' ? (
                                    <div className="flex flex-col items-center gap-1">
                                        <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                                            In Progress
                                        </Badge>
                                        {phase.progress > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                                {phase.progress}%
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <Badge variant="outline" className="text-slate-400 border-slate-200">
                                        Locked
                                    </Badge>
                                )}
                            </TableCell>
                        )) || Array(8).fill(null).map((_, idx) => (
                            <TableCell key={`empty-phase-${idx}`} className="text-center">
                                <Badge variant="outline" className="text-slate-400 border-slate-200">
                                    -
                                </Badge>
                            </TableCell>
                        ))}
                        <TableCell className="text-center">
                            <Badge variant="secondary" className="font-bold">
                                {item.overall_progress || 0}%
                            </Badge>
                        </TableCell>
                    </TableRow>
                );
            case 'tech_learning':
                const isExpanded = expandedRows.has(item.user_id);
                return (
                    <Fragment key={getRowKey('tech-learning')}>
                        <TableRow 
                            key={getRowKey('tech-learning')}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleRowExpand(item.user_id)}
                        >
                            <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    {item.user_name || '-'}
                                </div>
                            </TableCell>
                            <TableCell>{item.department || '-'}</TableCell>
                            <TableCell>
                                <Badge variant="outline">{item.total_courses || 0} course(s)</Badge>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div 
                                            className={cn(
                                                "h-full rounded-full transition-all",
                                                item.avg_progress >= 100 ? "bg-emerald-500" :
                                                item.avg_progress >= 50 ? "bg-blue-500" :
                                                item.avg_progress > 0 ? "bg-amber-500" : "bg-slate-300"
                                            )}
                                            style={{ width: `${Math.min(item.avg_progress || 0, 100)}%` }}
                                        />
                                    </div>
                                    <span className="text-sm font-medium">{item.avg_progress || 0}%</span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge 
                                    variant="secondary"
                                    className={item.completed_courses > 0 ? 'bg-emerald-500/20 text-emerald-700' : ''}
                                >
                                    {item.completed_courses || 0} / {item.total_courses || 0}
                                </Badge>
                            </TableCell>
                        </TableRow>
                        {/* Expanded course details */}
                        {isExpanded && item.courses && item.courses.map((course, courseIdx) => (
                            <TableRow 
                                key={`${getRowKey('tech-learning')}-course-${courseIdx}`}
                                className="bg-slate-50/50"
                            >
                                <TableCell className="pl-10 text-muted-foreground text-sm" colSpan={1}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div>
                                        {course.course_title}
                                    </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {course.course_category || '-'}
                                </TableCell>
                                <TableCell>
                                    <Badge 
                                        variant={course.status === 'Completed' ? 'default' : course.status === 'In Progress' ? 'secondary' : 'outline'}
                                        className={
                                            course.status === 'Completed' ? 'bg-emerald-500/20 text-emerald-700 text-xs' :
                                            course.status === 'In Progress' ? 'bg-blue-500/20 text-blue-700 text-xs' :
                                            'text-slate-500 text-xs'
                                        }
                                    >
                                        {course.status || 'Not Started'}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                            <div 
                                                className={cn(
                                                    "h-full rounded-full transition-all",
                                                    course.progress_percent >= 100 ? "bg-emerald-500" :
                                                    course.progress_percent >= 50 ? "bg-blue-500" :
                                                    course.progress_percent > 0 ? "bg-amber-500" : "bg-slate-300"
                                                )}
                                                style={{ width: `${Math.min(course.progress_percent || 0, 100)}%` }}
                                            />
                                        </div>
                                        <span className="text-xs text-muted-foreground">{course.progress_percent || 0}%</span>
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    {course.overall_score > 0 ? `${course.overall_score}%` : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </Fragment>
                );
            case 'finance_training':
                const isFinanceExpanded = expandedRows.has(item.user_id);
                return (
                    <Fragment key={getRowKey('finance-training')}>
                        <TableRow 
                            key={getRowKey('finance-training')}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleRowExpand(item.user_id)}
                        >
                            <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                    {isFinanceExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    {item.user_name || '-'}
                                </div>
                            </TableCell>
                            <TableCell>{item.department || '-'}</TableCell>
                            <TableCell>
                                <Badge variant="outline">{item.total_courses || 0} course(s)</Badge>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div 
                                            className={cn(
                                                "h-full rounded-full transition-all",
                                                item.avg_progress >= 100 ? "bg-emerald-500" :
                                                item.avg_progress >= 50 ? "bg-blue-500" :
                                                item.avg_progress > 0 ? "bg-amber-500" : "bg-slate-300"
                                            )}
                                            style={{ width: `${Math.min(item.avg_progress || 0, 100)}%` }}
                                        />
                                    </div>
                                    <span className="text-sm font-medium">{item.avg_progress || 0}%</span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge 
                                    variant="secondary"
                                    className={item.completed_courses > 0 ? 'bg-emerald-500/20 text-emerald-700' : ''}
                                >
                                    {item.completed_courses || 0} / {item.total_courses || 0}
                                </Badge>
                            </TableCell>
                        </TableRow>
                        {/* Expanded course details */}
                        {isFinanceExpanded && item.courses && item.courses.map((course, courseIdx) => (
                            <TableRow 
                                key={`${getRowKey('finance-training')}-course-${courseIdx}`}
                                className="bg-emerald-50/50"
                            >
                                <TableCell className="pl-10 text-muted-foreground text-sm" colSpan={1}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                                        {course.course_title}
                                    </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {course.course_category || '-'}
                                </TableCell>
                                <TableCell>
                                    <Badge 
                                        variant={course.status === 'Completed' ? 'default' : course.status === 'In Progress' ? 'secondary' : 'outline'}
                                        className={
                                            course.status === 'Completed' ? 'bg-emerald-500/20 text-emerald-700 text-xs' :
                                            course.status === 'In Progress' ? 'bg-blue-500/20 text-blue-700 text-xs' :
                                            'text-slate-500 text-xs'
                                        }
                                    >
                                        {course.status || 'Not Started'}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                            <div 
                                                className={cn(
                                                    "h-full rounded-full transition-all",
                                                    course.progress_percent >= 100 ? "bg-emerald-500" :
                                                    course.progress_percent >= 50 ? "bg-blue-500" :
                                                    course.progress_percent > 0 ? "bg-amber-500" : "bg-slate-300"
                                                )}
                                                style={{ width: `${Math.min(course.progress_percent || 0, 100)}%` }}
                                            />
                                        </div>
                                        <span className="text-xs text-muted-foreground">{course.progress_percent || 0}%</span>
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    {course.overall_score > 0 ? `${course.overall_score}%` : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </Fragment>
                );
            case 'simulations':
                return (
                    <TableRow 
                        key={getRowKey('sim')} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(item)}
                    >
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell>{item.scenario_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}</TableCell>
                        <TableCell>
                            <Badge 
                                variant="secondary" 
                                className={item.final_score >= 70 ? 'bg-green-100 text-green-700' : item.final_score > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}
                            >
                                {item.final_score || 0}%
                            </Badge>
                        </TableCell>
                        <TableCell>
                            <span className={item.voice_score >= 70 ? 'text-green-600 font-medium' : item.voice_score > 0 ? 'text-amber-600' : 'text-slate-400'} title={item.voice_score ? undefined : 'Typed session (no voice score)'}>
                                {item.voice_score || 0}%
                            </span>
                        </TableCell>
                        <TableCell>
                            <span className={item.communication_score >= 70 ? 'text-green-600 font-medium' : item.communication_score > 0 ? 'text-amber-600' : 'text-slate-400'}>
                                {item.communication_score || 0}%
                            </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.completed_at)}</TableCell>
                    </TableRow>
                );
            case 'job_simulations':
                return (
                    <TableRow 
                        key={getRowKey('job-sim')}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(item)}
                    >
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell>{item.skill_name || '-'}</TableCell>
                        <TableCell>{item.skill_category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}</TableCell>
                        <TableCell>
                            <Badge 
                                variant="secondary"
                                className={item.score >= 70 ? 'bg-green-100 text-green-700' : item.score > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}
                            >
                                {item.score || 0}%
                            </Badge>
                        </TableCell>
                        <TableCell>{item.simulation_type ? item.simulation_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : (item.lessons_completed || item.lesson_index || '-')}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.completed_at)}</TableCell>
                    </TableRow>
                );
            case 'inside_sales':
                return (
                    <TableRow key={getRowKey('inside-sales')}>
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell>Session {item.session_id || '-'}</TableCell>
                        <TableCell>
                            <Badge variant={item.status === 'completed' ? 'default' : 'secondary'}>
                                {item.status || '-'}
                            </Badge>
                        </TableCell>
                        <TableCell><Badge variant="secondary">{item.overall_score || 0}%</Badge></TableCell>
                        <TableCell>{item.quiz_score || 0}%</TableCell>
                        <TableCell>{item.simulation_score || 0}%</TableCell>
                        <TableCell>{item.time_spent_minutes || 0} min</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.completed_at)}</TableCell>
                    </TableRow>
                );
            case 'quality_tasks':
                return (
                    <TableRow key={getRowKey('quality')} data-testid={`quality-report-row-${index}`}>
                        <TableCell className="font-medium">
                            {item.user_name || '-'}
                            <p className="text-xs font-normal text-muted-foreground">{item.email}</p>
                        </TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell className="max-w-[260px]">
                            <p className="font-medium leading-snug">{item.course}</p>
                            <p className="text-xs text-muted-foreground">{item.module}{item.group ? ` · ${item.group}` : ''}</p>
                        </TableCell>
                        <TableCell data-testid={`quality-report-avg-${index}`}>
                            {item.avg_score === null ? <span className="text-muted-foreground">-</span> : (
                                <Badge variant="secondary" className={item.avg_score >= 70 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                    {item.avg_score}%
                                </Badge>
                            )}
                            <p className="mt-1 text-xs text-muted-foreground">{item.attempts} attempt{item.attempts === 1 ? '' : 's'}</p>
                        </TableCell>
                        <TableCell data-testid={`quality-report-completed-${index}`}>
                            {item.completed ? (
                                <>
                                    <Badge className="bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/30">Yes</Badge>
                                    <p className="mt-1 text-xs text-muted-foreground">{formatDate(item.completed_at)}</p>
                                </>
                            ) : item.missed ? (
                                <Badge variant="outline" className="border-red-300 text-red-600">Missed</Badge>
                            ) : (
                                <Badge variant="outline" className="text-slate-500">No</Badge>
                            )}
                        </TableCell>
                        <TableCell data-testid={`quality-report-assigned-by-${index}`}>{item.assigned_by_name || '-'}</TableCell>
                    </TableRow>
                );
            case 'sourcing_labs':
                return (
                    <TableRow 
                        key={getRowKey('sourcing')}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(item)}
                    >
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell>{item.lab_title || '-'}</TableCell>
                        <TableCell><Badge variant="outline">{item.lab_difficulty || '-'}</Badge></TableCell>
                        <TableCell>
                            <Badge 
                                variant="secondary"
                                className={item.overall_score >= 70 ? 'bg-green-100 text-green-700' : item.overall_score > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}
                            >
                                {item.overall_score || 0}%
                            </Badge>
                        </TableCell>
                        <TableCell>
                            <span className={item.accuracy_score >= 70 ? 'text-green-600 font-medium' : item.accuracy_score > 0 ? 'text-amber-600' : 'text-slate-400'}>
                                {item.accuracy_score || 0}%
                            </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.completed_at)}</TableCell>
                    </TableRow>
                );
            case 'evolve':
                return (
                    <TableRow 
                        key={getRowKey('evolve')}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(item)}
                    >
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{item.topic_name || '-'}</TableCell>
                        <TableCell>
                            <Badge 
                                variant="secondary"
                                className={item.overall_score >= 70 ? 'bg-green-100 text-green-700' : item.overall_score > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}
                            >
                                {item.overall_score || 0}%
                            </Badge>
                        </TableCell>
                        <TableCell>
                            <span className={item.content_clarity >= 70 ? 'text-green-600 font-medium' : item.content_clarity > 0 ? 'text-amber-600' : 'text-slate-400'}>
                                {item.content_clarity || 0}%
                            </span>
                        </TableCell>
                        <TableCell>
                            <span className={item.delivery_confidence >= 70 ? 'text-green-600 font-medium' : item.delivery_confidence > 0 ? 'text-amber-600' : 'text-slate-400'}>
                                {item.delivery_confidence || 0}%
                            </span>
                        </TableCell>
                        <TableCell>{item.slides_count || 0}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.completed_at)}</TableCell>
                    </TableRow>
                );
            case 'streaks':
                return (
                    <TableRow key={getRowKey('streak')}>
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell>
                            <div className="flex items-center gap-1">
                                <Flame className="h-4 w-4 text-orange-500" />
                                <span className="font-bold">{item.current_streak || 0}</span>
                            </div>
                        </TableCell>
                        <TableCell>{item.longest_streak || 0}</TableCell>
                        <TableCell>{item.total_xp || 0}</TableCell>
                        <TableCell><Badge variant="outline">{item.badges_count || 0}</Badge></TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.last_activity)}</TableCell>
                    </TableRow>
                );
            case 'eeoc_training': {
                const statusStyles = {
                    'Completed': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
                    'In progress': 'bg-sky-500/10 text-sky-600 border-sky-500/30',
                    'Opened': 'bg-amber-500/10 text-amber-600 border-amber-500/30',
                    'Not started': 'bg-muted text-muted-foreground',
                    'No access': 'bg-red-500/10 text-red-600 border-red-500/30'
                };
                return (
                    <TableRow key={getRowKey('eeoc')} data-testid={`eeoc-report-row-${index}`}>
                        <TableCell>
                            <p className="font-medium">{item.user_name || '-'}</p>
                            <p className="text-xs text-muted-foreground">{item.email}</p>
                        </TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell>
                            <Badge variant="outline" className={statusStyles[item.period_status]} data-testid={`eeoc-report-period-status-${index}`}>{item.period_status}</Badge>
                            {item.overdue && reportData?.is_current_period && (
                                <Badge variant="outline" className="ml-1 border-red-500/40 bg-red-500/10 text-red-600" data-testid={`eeoc-report-overdue-${index}`}>Overdue</Badge>
                            )}
                        </TableCell>
                        <TableCell className="tabular-nums" data-testid={`eeoc-report-period-modules-${index}`}>{item.period_modules_passed}/{item.total_modules}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{item.period_completed_at ? formatDate(item.period_completed_at) : '-'}</TableCell>
                        <TableCell>
                            <Badge variant="outline" className={statusStyles[item.status]}>{item.status}</Badge>
                            <p className="mt-1 text-xs text-muted-foreground">{item.completed_modules}/{item.total_modules} ever passed</p>
                        </TableCell>
                        <TableCell>{item.sessions_completed || 0}</TableCell>
                        <TableCell>{item.average_score ?? '-'}</TableCell>
                        <TableCell>
                            {item.latest_score === null || item.latest_score === undefined ? '-' : (
                                <span className={item.latest_passed ? 'font-medium text-emerald-600' : 'font-medium text-red-600'}>
                                    {item.latest_score} {item.latest_passed ? 'PASS' : 'FAIL'}
                                </span>
                            )}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`eeoc-report-trend-${index}`}>
                            {{ improving: <span className="inline-flex items-center gap-1 text-emerald-600"><TrendingUp className="h-3.5 w-3.5" />+{item.trend_delta}</span>,
                               declining: <span className="inline-flex items-center gap-1 text-red-600"><TrendingDown className="h-3.5 w-3.5" />{item.trend_delta}</span>,
                               steady: <span className="text-amber-600">Steady</span> }[item.trend] || <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.last_activity)}</TableCell>
                        <TableCell data-testid={`eeoc-report-reminders-${index}`}>
                            <EEOCNudgeCell row={item} index={index} isCurrent={reportData?.is_current_period} onDone={refreshQuietly} />
                        </TableCell>
                        <TableCell>
                            <Button size="sm" variant="outline" onClick={() => setEeocHistoryUser(item.user_id)} data-testid={`eeoc-history-btn-${index}`}>
                                <Eye className="h-3.5 w-3.5 mr-1" /> View
                            </Button>
                        </TableCell>
                    </TableRow>
                );
            }
            case 'last_login':
                return (
                    <TableRow key={getRowKey('login')}>
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell className="text-sm">{item.email || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell><Badge variant="outline">{item.role || '-'}</Badge></TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.last_login)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.created_at)}</TableCell>
                        <TableCell>
                            {item.is_locked ? (
                                <Badge variant="destructive">Locked</Badge>
                            ) : (
                                <Badge variant="secondary">Active</Badge>
                            )}
                        </TableCell>
                    </TableRow>
                );
            case 'user_activity':
                return (
                    <TableRow key={getRowKey('activity')}>
                        <TableCell className="font-medium">{item.user_name || '-'}</TableCell>
                        <TableCell>{item.department || '-'}</TableCell>
                        <TableCell>{item.simulations_completed || 0}</TableCell>
                        <TableCell>{item.job_simulations_completed || 0}</TableCell>
                        <TableCell>{item.sourcing_labs_completed || 0}</TableCell>
                        <TableCell>{item.evolve_sessions_completed || 0}</TableCell>
                        <TableCell><Badge variant="secondary">{item.total_activities || 0}</Badge></TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(item.last_login)}</TableCell>
                    </TableRow>
                );
            case 'monthly':
                return (
                    <TableRow key={`monthly-${item.month || index}`}>
                        <TableCell className="font-medium">{item.month_name || item.month}</TableCell>
                        <TableCell>{item.simulations || 0}</TableCell>
                        <TableCell>{item.job_simulations || 0}</TableCell>
                        <TableCell>{item.sourcing_labs || 0}</TableCell>
                        <TableCell>{item.evolve_sessions || 0}</TableCell>
                        <TableCell><Badge variant="secondary">{item.total || 0}</Badge></TableCell>
                    </TableRow>
                );
            default:
                return null;
        }
    };

    return (
        <MainLayout>
            <div className="space-y-6" data-testid="reports-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold font-heading flex items-center gap-3">
                            <FileText className="h-8 w-8 text-primary" />
                            Reports
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {isAdmin ? 'Generate and export comprehensive reports for all features' : 'View your activity reports'}
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <Button
                                onClick={handleGenerateScores}
                                disabled={generatingScores}
                                variant="outline"
                                className="gap-2"
                                data-testid="generate-scores-btn"
                            >
                                {generatingScores ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Zap className="h-4 w-4" />
                                )}
                                Generate Missing Scores
                            </Button>
                        )}
                        {activeTab !== 'report_archive' && !reportUser && (
                        <Button
                            onClick={handleExport}
                            disabled={exporting || loading || (!reportData?.data?.length && activeTab !== 'roleplay_labs')}
                            className="gap-2"
                            data-testid="export-btn"
                        >
                            {exporting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Download className="h-4 w-4" />
                            )}
                            Export to CSV
                        </Button>
                        )}
                    </div>
                </div>

                {/* Score Summary Cards (Admin Only) */}
                {isAdmin && scoreSummary && !reportUser && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="border-blue-200 bg-blue-50/50">
                            <CardContent className="pt-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Simulations</p>
                                        <div className="flex items-baseline gap-2">
                                            <p className="text-2xl font-bold">{scoreSummary.modules?.simulations?.total_sessions || 0}</p>
                                            <Badge variant="outline" className="text-xs">
                                                Avg: {scoreSummary.modules?.simulations?.average_score || 0}%
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {scoreSummary.modules?.simulations?.sessions_with_scores || 0} with scores
                                        </p>
                                    </div>
                                    <Mic className="h-8 w-8 text-blue-500 opacity-50" />
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-emerald-200 bg-emerald-50/50">
                            <CardContent className="pt-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Sourcing Labs</p>
                                        <div className="flex items-baseline gap-2">
                                            <p className="text-2xl font-bold">{scoreSummary.modules?.sourcing_labs?.total_sessions || 0}</p>
                                            <Badge variant="outline" className="text-xs">
                                                Avg: {scoreSummary.modules?.sourcing_labs?.average_score || 0}%
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {scoreSummary.modules?.sourcing_labs?.sessions_with_scores || 0} with scores
                                        </p>
                                    </div>
                                    <Search className="h-8 w-8 text-emerald-500 opacity-50" />
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-purple-200 bg-purple-50/50">
                            <CardContent className="pt-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Job Simulations</p>
                                        <div className="flex items-baseline gap-2">
                                            <p className="text-2xl font-bold">{scoreSummary.modules?.job_simulations?.total_sessions || 0}</p>
                                            <Badge variant="outline" className="text-xs">
                                                Avg: {scoreSummary.modules?.job_simulations?.average_score || 0}%
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {scoreSummary.modules?.job_simulations?.sessions_with_scores || 0} with scores
                                        </p>
                                    </div>
                                    <Briefcase className="h-8 w-8 text-purple-500 opacity-50" />
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-orange-200 bg-orange-50/50">
                            <CardContent className="pt-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Evolve AI</p>
                                        <div className="flex items-baseline gap-2">
                                            <p className="text-2xl font-bold">{scoreSummary.modules?.evolve_ai?.total_sessions || 0}</p>
                                            <Badge variant="outline" className="text-xs">
                                                Avg: {scoreSummary.modules?.evolve_ai?.average_score || 0}%
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {scoreSummary.modules?.evolve_ai?.sessions_with_scores || 0} with scores
                                        </p>
                                    </div>
                                    <Video className="h-8 w-8 text-orange-500 opacity-50" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Filters */}
                <Card className="border-dashed">
                    <CardContent className="pt-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <Select value={timeRange} onValueChange={setTimeRange}>
                                    <SelectTrigger className="w-36" data-testid="filter-time-range">
                                        <SelectValue placeholder="Time Range" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Time</SelectItem>
                                        <SelectItem value="7">Last 7 Days</SelectItem>
                                        <SelectItem value="30">Last 30 Days</SelectItem>
                                        <SelectItem value="90">Last 90 Days</SelectItem>
                                        <SelectItem value="365">Last Year</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {isAdmin && !reportUser && (
                                <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <Select value={department} onValueChange={setDepartment}>
                                        <SelectTrigger className="w-40" data-testid="filter-department">
                                            <SelectValue placeholder="Department" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Departments</SelectItem>
                                            {departments.map(dept => (
                                                <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            
                            {activeTab === 'evolve' && (
                                <div className="flex items-center gap-2">
                                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="month"
                                        value={month}
                                        onChange={(e) => setMonth(e.target.value)}
                                        className="w-40"
                                        placeholder="Select month"
                                        data-testid="filter-month"
                                    />
                                </div>
                            )}
                            
                            {!reportUser && (
                                <Button variant="outline" size="icon" onClick={fetchReport} data-testid="refresh-btn">
                                    <RefreshCw className="h-4 w-4" />
                                </Button>
                            )}

                            {isAdmin && (
                                <div className="sm:ml-auto w-full sm:w-auto">
                                    <UserSearch api={api} onSelect={setReportUser} />
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {reportUser ? (
                    <UserReportView
                        api={api}
                        user={reportUser}
                        days={timeRange}
                        reportConfig={reportConfig}
                        renderRow={renderTableRow}
                        onClose={() => setReportUser(null)}
                    />
                ) : (
                /* Report Type Tabs */
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent justify-start">
                        {availableTabs.map(([key, config]) => {
                            const Icon = config.icon;
                            return (
                                <TabsTrigger
                                    key={key}
                                    value={key}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg border",
                                        "data-[state=active]:border-primary data-[state=active]:bg-primary/5",
                                        "hover:bg-muted/50 transition-all"
                                    )}
                                    data-testid={`tab-${key}`}
                                >
                                    <Icon className={cn("h-4 w-4", config.color)} />
                                    <span className="text-sm font-medium">{config.label}</span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    {/* Report Content */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={cn("p-2 rounded-lg", currentConfig.bgColor)}>
                                        <CurrentIcon className={cn("h-5 w-5", currentConfig.color)} />
                                    </div>
                                    <div>
                                        <CardTitle>{activeTab === 'report_archive' ? currentConfig.label : `${currentConfig.label} Report`}</CardTitle>
                                        <CardDescription>
                                            {reportData?.summary ? (
                                                <span>
                                                    {reportData.summary.total_sessions !== undefined && 
                                                        `${reportData.summary.total_sessions} total sessions`}
                                                    {reportData.summary.total_users !== undefined && 
                                                        `${reportData.summary.total_sessions !== undefined ? ' • ' : ''}${reportData.summary.total_users} users`}
                                                    {reportData.summary.total_users_with_streaks !== undefined && 
                                                        `${reportData.summary.total_users_with_streaks} users with active streaks`}
                                                    {reportData.summary.average_score !== undefined && 
                                                        ` • Avg Score: ${reportData.summary.average_score}`}
                                                </span>
                                            ) : activeTab === 'report_archive' ? (
                                                'Saved copies of every report, one per day'
                                            ) : (
                                                'Loading...'
                                            )}
                                        </CardDescription>
                                    </div>
                                </div>
                                {reportData?.generated_at && (
                                    <Badge variant="outline" className="text-xs">
                                        Generated: {formatDate(reportData.generated_at)}
                                    </Badge>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {activeTab === 'eeoc_training' && reportData?.period && !loading && (
                                <EEOCReportControls
                                    data={reportData}
                                    period={eeocPeriod}
                                    onPeriodChange={setEeocPeriod}
                                    filter={eeocFilter}
                                    onFilterChange={setEeocFilter}
                                    department={department !== 'all' ? department : null}
                                    onNudged={refreshQuietly}
                                />
                            )}
                            {activeTab === 'report_archive' ? (
                                <ReportArchive />
                            ) : loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : activeTab === 'roleplay_labs' && reportData?.report_type === 'roleplay_labs' ? (
                                <RolePlayLabReport data={reportData} view={roleplayView} onViewChange={setRoleplayView} />
                            ) : reportData?.data?.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                {currentConfig.columns.map((col) => (
                                                    <TableHead key={`col-${col}`}>{col}</TableHead>
                                                ))}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(activeTab === 'eeoc_training' ? filterEEOCRows(reportData.data, eeocFilter) : reportData.data).map((item, idx) => renderTableRow(item, idx))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <CurrentIcon className={cn("h-12 w-12 mx-auto mb-4", currentConfig.color, "opacity-50")} />
                                    <p className="text-muted-foreground">No data available for this report</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Try adjusting your filters or time range
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Summary Cards */}
                    {reportData?.summary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {reportData.summary.total_sessions !== undefined && (
                                <Card>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-3">
                                            <div className={cn("p-2 rounded-lg", currentConfig.bgColor)}>
                                                <Target className={cn("h-4 w-4", currentConfig.color)} />
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Total Sessions</p>
                                                <p className="text-2xl font-bold">{reportData.summary.total_sessions}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            {reportData.summary.unique_users !== undefined && (
                                <Card>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-blue-500/10">
                                                <Users className="h-4 w-4 text-blue-500" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Unique Users</p>
                                                <p className="text-2xl font-bold">{reportData.summary.unique_users}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            {reportData.summary.average_score !== undefined && (
                                <Card>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-emerald-500/10">
                                                <TrendingUp className="h-4 w-4 text-emerald-500" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Average Score</p>
                                                <p className="text-2xl font-bold">{reportData.summary.average_score}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            {reportData.summary.max_current_streak !== undefined && (
                                <Card>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-orange-500/10">
                                                <Flame className="h-4 w-4 text-orange-500" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Max Streak</p>
                                                <p className="text-2xl font-bold">{reportData.summary.max_current_streak} days</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            {reportData.totals && (
                                <Card className="col-span-2 md:col-span-4">
                                    <CardContent className="pt-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground">Year Total</span>
                                            <div className="flex items-center gap-4">
                                                <span>Simulations: <strong>{reportData.totals.simulations}</strong></span>
                                                <span>Job Sims: <strong>{reportData.totals.job_simulations}</strong></span>
                                                <span>Sourcing: <strong>{reportData.totals.sourcing_labs}</strong></span>
                                                <span>EVOLVE: <strong>{reportData.totals.evolve_sessions}</strong></span>
                                                <Badge variant="secondary" className="text-lg">Total: {reportData.totals.total}</Badge>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </Tabs>
                )}

                {/* Detail Modal */}
                <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Eye className="h-5 w-5" />
                                Session Details
                            </DialogTitle>
                            <DialogDescription>
                                {selectedRow?.user_name && `${selectedRow.user_name}'s session`}
                                {selectedRow?.completed_at && ` • ${formatDate(selectedRow.completed_at)}`}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedRow && (
                            <ScrollArea className="max-h-[500px]">
                                <div className="space-y-4">
                                    {/* User Info */}
                                    <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/50">
                                        <div>
                                            <p className="text-sm text-muted-foreground">User</p>
                                            <p className="font-medium">{selectedRow.user_name || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Email</p>
                                            <p className="font-medium">{selectedRow.email || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Department</p>
                                            <p className="font-medium">{selectedRow.department || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Designation</p>
                                            <p className="font-medium">{selectedRow.designation || '-'}</p>
                                        </div>
                                    </div>

                                    {/* Scores Section */}
                                    <div className="p-4 rounded-lg border">
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                            <Target className="h-4 w-4" />
                                            Scores
                                        </h4>
                                        <div className="grid grid-cols-3 gap-4">
                                            {/* Overall/Main Score */}
                                            <div className="text-center p-3 rounded-lg bg-primary/10">
                                                <p className="text-3xl font-bold text-primary">
                                                    {selectedRow.final_score || selectedRow.overall_score || selectedRow.score || 0}%
                                                </p>
                                                <p className="text-sm text-muted-foreground">Overall Score</p>
                                            </div>
                                            
                                            {/* Voice/Communication for Simulations */}
                                            {selectedRow.voice_score !== undefined && (
                                                <div className="text-center p-3 rounded-lg bg-blue-50">
                                                    <p className="text-2xl font-bold text-blue-600">
                                                        {selectedRow.voice_score || 0}%
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">Voice Score</p>
                                                </div>
                                            )}
                                            {selectedRow.communication_score !== undefined && (
                                                <div className="text-center p-3 rounded-lg bg-purple-50">
                                                    <p className="text-2xl font-bold text-purple-600">
                                                        {selectedRow.communication_score || 0}%
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">Communication</p>
                                                </div>
                                            )}
                                            
                                            {/* Accuracy/Completeness for Sourcing Labs */}
                                            {selectedRow.accuracy_score !== undefined && (
                                                <div className="text-center p-3 rounded-lg bg-emerald-50">
                                                    <p className="text-2xl font-bold text-emerald-600">
                                                        {selectedRow.accuracy_score || 0}%
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">Accuracy</p>
                                                </div>
                                            )}
                                            {selectedRow.completeness_score !== undefined && (
                                                <div className="text-center p-3 rounded-lg bg-cyan-50">
                                                    <p className="text-2xl font-bold text-cyan-600">
                                                        {selectedRow.completeness_score || 0}%
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">Completeness</p>
                                                </div>
                                            )}
                                            
                                            {/* Evolve Scores */}
                                            {selectedRow.content_clarity !== undefined && (
                                                <div className="text-center p-3 rounded-lg bg-orange-50">
                                                    <p className="text-2xl font-bold text-orange-600">
                                                        {selectedRow.content_clarity || 0}%
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">Content Clarity</p>
                                                </div>
                                            )}
                                            {selectedRow.delivery_confidence !== undefined && (
                                                <div className="text-center p-3 rounded-lg bg-amber-50">
                                                    <p className="text-2xl font-bold text-amber-600">
                                                        {selectedRow.delivery_confidence || 0}%
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">Confidence</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Raw Scores Object (if available) */}
                                    {selectedRow.scores && Object.keys(selectedRow.scores).length > 0 && (
                                        <div className="p-4 rounded-lg border">
                                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                                                <BarChart3 className="h-4 w-4" />
                                                Detailed Breakdown
                                            </h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                {Object.entries(selectedRow.scores)
                                                    .filter(([key]) => typeof selectedRow.scores[key] === 'number')
                                                    .map(([key, value]) => (
                                                        <div key={key} className="flex items-center justify-between py-1 border-b border-dashed last:border-0">
                                                            <span className="text-sm text-muted-foreground capitalize">
                                                                {key.replace(/_/g, ' ')}
                                                            </span>
                                                            <Badge variant="outline">{value}%</Badge>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    )}

                                    {/* Session Details */}
                                    <div className="p-4 rounded-lg border">
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                            <Clock className="h-4 w-4" />
                                            Session Info
                                        </h4>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            {selectedRow.scenario_type && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Scenario</span>
                                                    <span className="font-medium">{selectedRow.scenario_type.replace(/_/g, ' ')}</span>
                                                </div>
                                            )}
                                            {selectedRow.difficulty && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Difficulty</span>
                                                    <Badge variant="outline">{selectedRow.difficulty}</Badge>
                                                </div>
                                            )}
                                            {selectedRow.lab_title && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Lab</span>
                                                    <span className="font-medium">{selectedRow.lab_title}</span>
                                                </div>
                                            )}
                                            {selectedRow.skill_name && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Skill</span>
                                                    <span className="font-medium">{selectedRow.skill_name}</span>
                                                </div>
                                            )}
                                            {selectedRow.candidates_found !== undefined && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Candidates Found</span>
                                                    <span className="font-medium">{selectedRow.candidates_found}</span>
                                                </div>
                                            )}
                                            {selectedRow.time_taken && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Time Taken</span>
                                                    <span className="font-medium">{selectedRow.time_taken} min</span>
                                                </div>
                                            )}
                                            {selectedRow.exchanges && (
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Exchanges</span>
                                                    <span className="font-medium">{selectedRow.exchanges?.length || 0}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </ScrollArea>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
            <EEOCHistoryDialog
                api={api}
                userId={eeocHistoryUser}
                open={Boolean(eeocHistoryUser)}
                onOpenChange={(open) => !open && setEeocHistoryUser(null)}
            />
        </MainLayout>
    );
};

export default Reports;
